import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import Swal from "sweetalert2";
import { patchRequest } from "services/api";
import { fetchTrfEntries, normalizeTrfText } from "./trf-duplicate-check";
import { getBulkFailures, buildBulkFailureHtml, runBulkRequest } from "./bulk-api";

const formatNumber = (value) => Number(value || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });

const formatDateLabel = (dateValue) => new Date(`${dateValue}T12:00:00`).toLocaleDateString("id-ID", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

// Kirim total harian Accounting V2 ke Menu Pembukuan secara MANUAL (atas konfirmasi admin).
// 1 baris per tanggal per tab: kalau tanggal itu sudah pernah dikirim, barisnya diperbarui (bukan dobel).
const AccountingV2BookkeepingSyncModal = ({ show, date, values, hasUnsavedChanges, onHide }) => {
  const [existingEntries, setExistingEntries] = useState([]);
  const [checkState, setCheckState] = useState({ loading: false, error: "" });
  const [included, setIncluded] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show || !date) return undefined;
    let cancelled = false;
    setCheckState({ loading: true, error: "" });
    setExistingEntries([]);
    fetchTrfEntries({ sections: values.map((value) => value.section), start: date, end: date })
      .then((entries) => {
        if (!cancelled) setExistingEntries(entries);
      })
      .catch((error) => {
        if (!cancelled) setCheckState({ loading: false, error: error?.message || "Gagal mengecek data Pembukuan." });
      })
      .finally(() => {
        if (!cancelled) setCheckState((current) => ({ ...current, loading: false }));
      });
    return () => {
      cancelled = true;
    };
  }, [show, date, values]);

  const rows = useMemo(() => values.map((value) => {
    const existing = existingEntries.find((entry) => (
      entry.section === value.section
      && entry.date === date
      && normalizeTrfText(entry.text) === normalizeTrfText(value.name)
    ));
    const amount = Math.round(Number(value.amount) || 0);
    let status = "new";
    if (existing) status = Math.round(existing.amount) === amount ? "same" : "update";
    return { ...value, amount, existing, status };
  }), [values, existingEntries, date]);

  // Default centang: yang baru / berubah dan nominalnya bukan 0.
  useEffect(() => {
    if (!show || checkState.loading) return;
    setIncluded(Object.fromEntries(rows.map((row) => [row.section, row.status !== "same" && row.amount !== 0])));
  }, [show, checkState.loading, rows]);

  const selectedRows = rows.filter((row) => included[row.section]);

  const handleSubmit = async () => {
    if (selectedRows.length === 0) {
      Swal.fire("Tidak ada yang dikirim", "Centang minimal 1 data untuk dikirim ke Pembukuan.", "info");
      return;
    }
    setSaving(true);
    const failures = [];
    let savedCount = 0;
    try {
      const buildPayload = (row) => ({
        date,
        section: row.section,
        entry_type: "debit",
        name: row.name,
        amount: row.amount,
        description: row.name,
        notes: "Dikirim dari Accounting V2",
      });

      // Update baris yang sudah ada (maks. 3, satu per tab).
      const updates = selectedRows.filter((row) => row.existing);
      for (const row of updates) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await patchRequest(`/accounting/additional-transactions/${row.existing.id}`, buildPayload(row));
          savedCount += 1;
        } catch (error) {
          failures.push({ label: row.label, reason: error?.message || "Gagal diperbarui" });
        }
      }

      // Baris baru: 1 request bulk.
      const creates = selectedRows.filter((row) => !row.existing);
      if (creates.length) {
        const response = await runBulkRequest({
          url: "/accounting/additional-transactions/bulk",
          method: "POST",
          payload: { atomic: false, items: creates.map(buildPayload) },
        });
        savedCount += response.results.filter((result) => result.status === "ok").length;
        getBulkFailures(response.results, creates, (row) => row?.label || "-")
          .forEach((failure) => failures.push({ label: failure.label, reason: failure.message || failure.reason }));
      }
    } catch (error) {
      failures.push({ label: "Pembukuan", reason: error?.message || "Gagal mengirim data" });
    } finally {
      setSaving(false);
    }

    if (failures.length === 0) {
      Swal.fire("Berhasil", `${savedCount} data terkirim ke Pembukuan (${formatDateLabel(date)}).`, "success");
      onHide();
      return;
    }
    Swal.fire({
      icon: savedCount ? "warning" : "error",
      title: `${savedCount} terkirim, ${failures.length} gagal`,
      html: buildBulkFailureHtml(failures.map((failure) => ({ label: failure.label, reason: failure.reason }))),
    });
  };

  return (
    <Modal show={show} onHide={saving ? undefined : onHide} centered>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>Kirim ke Pembukuan</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="small text-muted mb-2">
          Tanggal <b>{date ? formatDateLabel(date) : "-"}</b>. Nilai diambil dari hasil kalkulasi Accounting V2 tanggal ini,
          disimpan sebagai <b>Debet</b>, 1 baris per tanggal (kirim ulang = diperbarui, tidak dobel).
        </div>
        {hasUnsavedChanges && (
          <Alert variant="warning" className="py-2 small">
            Masih ada perubahan di tabel yang sedang disimpan. Tunggu sampai selesai supaya nilai yang dikirim sudah final.
          </Alert>
        )}
        {checkState.error && (
          <Alert variant="warning" className="py-2 small">
            Tidak bisa mengecek data yang sudah ada ({checkState.error}). Mengirim sekarang bisa membuat baris dobel.
          </Alert>
        )}
        <Table size="sm" bordered className="align-middle mb-0" style={{ fontSize: 13 }}>
          <thead className="table-light">
            <tr>
              <th style={{ width: 36 }} />
              <th>Tab Pembukuan</th>
              <th className="text-end">Nominal</th>
              <th style={{ width: 150 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.section} className={included[row.section] ? "" : "text-muted"}>
                <td className="text-center">
                  <Form.Check
                    type="checkbox"
                    aria-label={`Kirim ${row.label}`}
                    checked={Boolean(included[row.section])}
                    onChange={(event) => setIncluded((current) => ({ ...current, [row.section]: event.target.checked }))}
                    disabled={saving || checkState.loading}
                  />
                </td>
                <td>
                  <div className="fw-bold">{row.label}</div>
                  <div className="small text-muted">{row.hint}</div>
                </td>
                <td className="text-end fw-bold" style={{ fontVariantNumeric: "tabular-nums" }}>{formatNumber(row.amount)}</td>
                <td>
                  {checkState.loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : row.status === "new" ? (
                    <Badge bg="success">Baru</Badge>
                  ) : row.status === "same" ? (
                    <Badge bg="secondary">Sudah terkirim, sama</Badge>
                  ) : (
                    <div>
                      <Badge bg="warning" text="dark">Diperbarui</Badge>
                      <div className="small text-muted">sebelumnya {formatNumber(row.existing.amount)}</div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" className="border" onClick={onHide} disabled={saving}>Batal</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={saving || checkState.loading || selectedRows.length === 0}>
          {saving ? <><Spinner animation="border" size="sm" className="me-1" /> Mengirim...</> : `Kirim ${selectedRows.length} data`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AccountingV2BookkeepingSyncModal;
